import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { useInRouterContext, useNavigate } from "react-router-dom";
import { Alert, App as AntdApp, Button, Drawer, Result, Skeleton } from "antd";
import { ApiContractError, ApiProblem, createRequestId } from "../../api/httpClient";
import { useActorContext } from "../../auth/ActorContext";
import { PageHeading } from "../../components/PageHeading";
import { getOrganizationTree } from "../administration/organizationAdminApi";
import { CreateUserAccountModal, type CreateUserAccountValues } from "./CreateUserAccountModal";
import { UserAccountLifecycleModal } from "./UserAccountLifecycleModal";
import { UserAccountPasswordResetModal } from "./UserAccountPasswordResetModal";
import type { AccountStatusAction } from "./UserAccountDetailDrawer";
import {
  activateUserAccount,
  createUserAccount,
  getUserAccount,
  identityAcceptedMessage,
  IDENTITY_POLL_INTERVAL_MS,
  lifecycleSuccessMessage,
  listUserAccounts,
  LIFECYCLE_ACTION_SOURCE,
  lockUserAccount,
  requestIdpProfileSynchronization,
  requestIdpProvisioning,
  requestPasswordResetEmail,
  unlockUserAccount,
  type AccountCommandError,
  type CreateUserAccountRequest,
  type IdentityActionKind,
  type LifecycleActionKind,
  type UserAccountAdminDetail,
  type UserAccountAdminPage,
  type UserAccountAdminListItem,
} from "./userAccountAdminApi";
import { UserAccountDetailDrawer } from "./UserAccountDetailDrawer";
import { UserAccountTable, type StatusFilter } from "./UserAccountTable";
import "./user-accounts.css";

const NCC_ADMIN_ROLE = "NCC_ADMIN";
const PAGE_SIZE = 10;

type ListState = Readonly<{
  status: "loading" | "ready" | "error";
  data: readonly UserAccountAdminListItem[];
  meta: UserAccountAdminPage["meta"] | null;
  requestId?: string | undefined;
}>;

type DetailState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ready"; detail: UserAccountAdminDetail }
  | { status: "error"; requestId?: string | undefined };

function isAbortError(error: unknown): boolean {
  return typeof error === "object" && error !== null && (error as { name?: string }).name === "AbortError";
}

function requestIdOf(error: unknown): string | undefined {
  if (error instanceof ApiProblem) return error.problem.requestId;
  if (error instanceof ApiContractError) return error.requestId;
  return undefined;
}

function errorCodeOf(error: unknown): string {
  return error instanceof ApiProblem ? error.problem.code : "UNKNOWN";
}

type CreateModalState = Readonly<{
  idempotencyKey: string;
  submitting: boolean;
  error: AccountCommandError | null;
}>;

type LifecycleModalState = Readonly<{
  action: LifecycleActionKind;
  accountName: string;
  idempotencyKey: string;
  submitting: boolean;
  error: AccountCommandError | null;
  open: boolean;
}>;

// S2.3.5 — state modal xác nhận gửi email đặt lại mật khẩu (§13.2).
type PasswordResetModalState = Readonly<{
  idempotencyKey: string;
  submitting: boolean;
  error: AccountCommandError | null;
  open: boolean;
}>;

export function UserAccountsPage() {
  const state = useActorContext();

  if (state.status === "loading") {
    return (
      <div role="status" aria-label="Đang tải tài khoản người dùng">
        <Skeleton active paragraph={{ rows: 8 }} />
      </div>
    );
  }

  const isAdmin =
    (state.status === "ready" || state.status === "refreshing" || state.status === "stale") &&
    state.actor.grants.some((grant) => grant.roleCode === NCC_ADMIN_ROLE);

  if (!isAdmin) {
    return (
      <Result
        status="403"
        title="Bạn không có quyền quản trị tài khoản"
        subTitle="Màn hình này chỉ dành cho quản trị hệ thống. Liên hệ quản trị viên hệ thống để được cấp quyền."
      />
    );
  }

  return (
    <AntdApp>
      <AdminUserAccountsContent />
    </AntdApp>
  );
}

function RoleNavActionRouter({ accountId, children }: { readonly accountId: string; readonly children: (navigate: () => void) => ReactNode }) {
  const navigate = useNavigate();
  return (
    <>
      {children(() => {
        void navigate(`/administration/roles-scopes?accountId=${encodeURIComponent(accountId)}`);
      })}
    </>
  );
}

function RoleNavActionWrapper({ accountId, children }: { readonly accountId: string; readonly children: (navigate: () => void) => ReactNode }) {
  const inRouter = useInRouterContext();
  if (inRouter) {
    return <RoleNavActionRouter accountId={accountId}>{children}</RoleNavActionRouter>;
  }
  return <>{children(() => window.location.assign(`/administration/roles-scopes?accountId=${encodeURIComponent(accountId)}`))}</>;
}

function AdminUserAccountsContent() {
  const { message: messageApi } = AntdApp.useApp();
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(undefined);
  const [orgFilter, setOrgFilter] = useState<string | undefined>(undefined);
  const [page, setPage] = useState(1);
  const [organizationOptions, setOrganizationOptions] = useState<readonly { value: string; label: string }[]>([]);
  const [listState, setListState] = useState<ListState>({ status: "loading", data: [], meta: null });
  const [listNonce, setListNonce] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detailState, setDetailState] = useState<DetailState>({ status: "idle" });
  const [detailNonce, setDetailNonce] = useState(0);
  const [createModalState, setCreateModalState] = useState<CreateModalState | null>(null);
  const [lifecycleModalState, setLifecycleModalState] = useState<LifecycleModalState | null>(null);
  // S2.3.5 — pending cho lệnh identity (mutation chỉ chạy từ event handler, §14).
  const [identityPending, setIdentityPending] = useState<IdentityActionKind | null>(null);
  const [passwordResetModalState, setPasswordResetModalState] = useState<PasswordResetModalState | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    getOrganizationTree(undefined, controller.signal)
      .then((nodes) =>
        setOrganizationOptions(nodes.map((node) => ({ value: node.id, label: node.name }))),
      )
      .catch(() => undefined);
    return () => controller.abort();
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setListState((prev) => ({ status: "loading", data: prev.data, meta: prev.meta }));
    const listParams: Parameters<typeof listUserAccounts>[0] = {
      // UI phân trang 1-based (antd), API contract zero-based → quy đổi ngay tại biên gọi.
      page: page - 1,
      size: PAGE_SIZE,
      q: query,
      ...(statusFilter !== undefined ? { status: statusFilter } : {}),
      ...(orgFilter !== undefined ? { organizationId: orgFilter } : {}),
    };
    listUserAccounts(listParams, controller.signal)
      .then((pageData) => {
        if (controller.signal.aborted) return;
        setListState({ status: "ready", data: pageData.data, meta: pageData.meta });
        setSelectedId((prev) => (prev && pageData.data.some((item) => item.id === prev) ? prev : null));
      })
      .catch((error: unknown) => {
        if (isAbortError(error)) return;
        setListState((prev) => ({ status: "error", data: prev.data, meta: prev.meta, requestId: requestIdOf(error) }));
      });
    return () => controller.abort();
  }, [query, statusFilter, orgFilter, page, listNonce]);

  useEffect(() => {
    if (!selectedId) {
      setDetailState({ status: "idle" });
      return;
    }
    const controller = new AbortController();
    setDetailState({ status: "loading" });
    getUserAccount(selectedId, controller.signal)
      .then((detail) => {
        if (controller.signal.aborted) return;
        setDetailState({ status: "ready", detail });
      })
      .catch((error: unknown) => {
        if (isAbortError(error)) return;
        setDetailState({ status: "error", requestId: requestIdOf(error) });
      });
    return () => controller.abort();
  }, [selectedId, detailNonce]);

  // §14 — polling PROCESSING: CHỈ re-fetch detail đang mở, dừng khi Drawer đóng
  // (selectedId=null) hoặc delivery state chuyển terminal. Không poll danh sách.
  const openIdentityStatus =
    detailState.status === "ready" ? detailState.detail.identityDeliveryStatus : undefined;
  useEffect(() => {
    if (!selectedId || openIdentityStatus !== "PROCESSING") return;
    const timer = setInterval(() => setDetailNonce((nonce) => nonce + 1), IDENTITY_POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [selectedId, openIdentityStatus]);

  const handleSearch = useCallback((nextQuery: string) => {
    setQuery(nextQuery);
    setPage(1);
  }, []);

  const handleStatusChange = useCallback((nextStatus: StatusFilter) => {
    setStatusFilter(nextStatus);
    setPage(1);
  }, []);

  const handleOrganizationChange = useCallback((nextOrganizationId: string | undefined) => {
    setOrgFilter(nextOrganizationId);
    setPage(1);
  }, []);

  const handlePageChange = useCallback((nextPage: number) => {
    setPage(nextPage);
  }, []);

  const handleClearFilters = useCallback(() => {
    setQuery("");
    setStatusFilter(undefined);
    setOrgFilter(undefined);
    setPage(1);
  }, []);

  const handleOpenCreate = useCallback(() => {
    setCreateModalState({ idempotencyKey: createRequestId(), submitting: false, error: null });
  }, []);

  const handleCancelCreate = useCallback(() => setCreateModalState(null), []);

  const handleCreateSubmit = useCallback(
    (values: CreateUserAccountValues) => {
      if (!createModalState) return;
      const key = createModalState.idempotencyKey;
      setCreateModalState((prev) => (prev ? { ...prev, submitting: true, error: null } : prev));
      const payload: CreateUserAccountRequest = {
        username: values.username,
        homeOrganizationId: values.homeOrganizationId,
        ...(values.personId ? { personId: values.personId } : {}),
        ...(values.displayName ? { displayName: values.displayName } : {}),
      };
      createUserAccount(payload, key)
        .then(() => {
          setCreateModalState(null);
          void messageApi.success("Tạo tài khoản thành công");
          setListNonce((nonce) => nonce + 1);
        })
        .catch((error: unknown) => {
          if (isAbortError(error)) return;
          setCreateModalState((prev) =>
            prev
              ? { ...prev, submitting: false, error: { code: errorCodeOf(error), requestId: requestIdOf(error) } }
              : prev,
          );
        });
    },
    [createModalState, messageApi],
  );

  const handleLifecycleAction = useCallback(
    (action: Readonly<{ kind: LifecycleActionKind }>) => {
      if (detailState.status !== "ready") return;
      setLifecycleModalState({
        action: action.kind,
        accountName: detailState.detail.personDisplayName,
        idempotencyKey: createRequestId(),
        submitting: false,
        error: null,
        open: true,
      });
    },
    [detailState],
  );

  // --- S2.3.5 — identity delivery commands (202 bất đồng bộ, §13.2/§14) ---

  const runIdentityCommand = useCallback(
    (kind: IdentityActionKind) => {
      if (detailState.status !== "ready" || identityPending !== null) return;
      const detail = detailState.detail;
      // Key mới cho mỗi intent, cùng convention với create/activate/lock.
      const key = createRequestId();
      setIdentityPending(kind);
      // provision và retry đi qua CÙNG command provision với expectedVersion của
      // detail đang tải — guard stale-detail theo convention S2.3.4.
      const command =
        kind === "synchronize"
          ? requestIdpProfileSynchronization(detail.id, key)
          : requestIdpProvisioning(detail.id, { expectedVersion: detail.version }, key);
      command
        .then(() => {
          void messageApi.success(identityAcceptedMessage(kind));
          // 202 chỉ ảnh hưởng detail đang mở — không reload toàn danh sách.
          setDetailNonce((nonce) => nonce + 1);
        })
        .catch((error: unknown) => {
          if (isAbortError(error)) return;
          const requestId = requestIdOf(error);
          void messageApi.error(
            requestId ? `Không thể thực hiện thao tác. Mã hỗ trợ: ${requestId}` : "Không thể thực hiện thao tác",
          );
        })
        .finally(() => setIdentityPending(null));
    },
    [detailState, identityPending, messageApi],
  );

  const handleDrawerAction = useCallback(
    (action: AccountStatusAction) => {
      switch (action.kind) {
        case "activate":
        case "lock":
        case "unlock":
          handleLifecycleAction(action);
          return;
        case "provision":
        case "retry":
        case "synchronize":
          runIdentityCommand(action.kind);
          return;
        case "request-password-reset":
          // Intent mới → idempotency key mới; POST chỉ chạy sau khi xác nhận trong modal.
          setPasswordResetModalState({ idempotencyKey: createRequestId(), submitting: false, error: null, open: true });
          return;
      }
    },
    [handleLifecycleAction, runIdentityCommand],
  );

  // Đóng bằng open=false (không unmount) để antd trả focus về menu item trigger của Drawer.
  const handleCancelPasswordReset = useCallback(
    () => setPasswordResetModalState((prev) => (prev ? { ...prev, open: false } : prev)),
    [],
  );

  const handlePasswordResetConfirm = useCallback(() => {
    if (!passwordResetModalState || !passwordResetModalState.open || detailState.status !== "ready") return;
    const detail = detailState.detail;
    const key = passwordResetModalState.idempotencyKey;
    setPasswordResetModalState((prev) => (prev ? { ...prev, submitting: true, error: null } : prev));
    requestPasswordResetEmail(detail.id, key)
      .then(() => {
        setPasswordResetModalState((prev) => (prev ? { ...prev, open: false, submitting: false } : prev));
        void messageApi.success(identityAcceptedMessage("request-password-reset"));
        setDetailNonce((nonce) => nonce + 1);
      })
      .catch((error: unknown) => {
        if (isAbortError(error)) return;
        setPasswordResetModalState((prev) =>
          prev
            ? { ...prev, submitting: false, error: { code: errorCodeOf(error), requestId: requestIdOf(error) } }
            : prev,
        );
      });
  }, [passwordResetModalState, detailState, messageApi]);

  // Đóng bằng open=false (không unmount) để antd trả focus về trigger của Drawer.
  const handleCancelLifecycle = useCallback(
    () => setLifecycleModalState((prev) => (prev ? { ...prev, open: false } : prev)),
    [],
  );

  const handleLifecycleSubmit = useCallback(
    (reason: string | undefined) => {
      if (!lifecycleModalState || !lifecycleModalState.open || detailState.status !== "ready") return;
      const detail = detailState.detail;
      const key = lifecycleModalState.idempotencyKey;
      setLifecycleModalState((prev) => (prev ? { ...prev, submitting: true, error: null } : prev));
      const command =
        lifecycleModalState.action === "activate"
          ? activateUserAccount(detail.id, { expectedVersion: detail.version }, key)
          : lifecycleModalState.action === "lock"
            ? lockUserAccount(detail.id, { expectedVersion: detail.version, reason: reason ?? "" }, key)
            : unlockUserAccount(detail.id, { expectedVersion: detail.version, reason: reason ?? "" }, key);
      command
        .then(() => {
          setLifecycleModalState((prev) => (prev ? { ...prev, open: false } : prev));
          void messageApi.success(lifecycleSuccessMessage(lifecycleModalState.action));
          setListNonce((nonce) => nonce + 1);
          setDetailNonce((nonce) => nonce + 1);
        })
        .catch((error: unknown) => {
          if (isAbortError(error)) return;
          setLifecycleModalState((prev) =>
            prev
              ? { ...prev, submitting: false, error: { code: errorCodeOf(error), requestId: requestIdOf(error) } }
              : prev,
          );
        });
    },
    [lifecycleModalState, detailState, messageApi],
  );

  // Guard cho reload: response cũ không được ghi đè selection mới (constraint "stale responses
  // must not overwrite new filters/selections") — dùng ref id + AbortController.
  const selectedIdRef = useRef<string | null>(selectedId);
  useEffect(() => {
    selectedIdRef.current = selectedId;
  }, [selectedId]);
  const reloadControllerRef = useRef<AbortController | null>(null);
  useEffect(() => () => reloadControllerRef.current?.abort(), []);

  const handleReloadLifecycle = useCallback(async () => {
    if (!selectedId || !lifecycleModalState) return;
    const requestedId = selectedId;
    const { action } = lifecycleModalState;
    reloadControllerRef.current?.abort();
    const controller = new AbortController();
    reloadControllerRef.current = controller;
    try {
      const freshDetail = await getUserAccount(requestedId, controller.signal);
      if (controller.signal.aborted || selectedIdRef.current !== requestedId) return;
      setDetailState({ status: "ready", detail: freshDetail });
      setListNonce((nonce) => nonce + 1);
      if (freshDetail.status !== LIFECYCLE_ACTION_SOURCE[action]) {
        // Trạng thái đã đổi → action cũ không còn hợp lệ: đóng modal, không cho gửi transition stale.
        setLifecycleModalState((prev) => (prev ? { ...prev, open: false } : prev));
        void messageApi.info("Trạng thái tài khoản đã thay đổi");
        return;
      }
      setLifecycleModalState((prev) => (prev ? { ...prev, error: null } : prev));
    } catch {
      // Reload thất bại: giữ nguyên alert xung đột để người dùng bấm "Tải lại thông tin" lần nữa.
    }
  }, [selectedId, lifecycleModalState, messageApi]);

  const showPageSkeleton = listState.status === "loading" && listState.data.length === 0;
  const tableLoading = listState.status === "loading" && !showPageSkeleton;
  const drawerOpen = selectedId !== null;
  const listIsError = listState.status === "error";

  return (
    <>
      <PageHeading
        title="Tài khoản người dùng"
        description="Tìm và đánh giá tài khoản đăng nhập theo trạng thái, trạng thái liên kết và đơn vị công tác, sau đó thực hiện thao tác vòng đời phù hợp."
        action={
          <Button type="primary" onClick={handleOpenCreate}>
            Tạo tài khoản
          </Button>
        }
      />
      {showPageSkeleton && (
        <div role="status" aria-label="Đang tải danh sách tài khoản">
          <Skeleton active paragraph={{ rows: 8 }} />
        </div>
      )}
      {listIsError && (
        <Alert
          type="error"
          showIcon
          role="alert"
          title="Không thể tải danh sách tài khoản"
          description={listState.requestId ? `Mã hỗ trợ: ${listState.requestId}` : undefined}
          action={<Button onClick={() => setListNonce((nonce) => nonce + 1)}>Thử lại</Button>}
        />
      )}
      {(listState.status === "ready" || tableLoading) && (
        <UserAccountTable
          data={listState.data}
          loading={tableLoading}
          selectedId={selectedId}
          pagination={{
            current: (listState.meta?.page ?? 0) + 1,
            pageSize: PAGE_SIZE,
            total: listState.meta?.totalElements ?? 0,
          }}
          filters={{ q: query, status: statusFilter, organizationId: orgFilter }}
          organizationOptions={organizationOptions}
          onSelectRow={setSelectedId}
          onChangeSearch={handleSearch}
          onChangeStatus={handleStatusChange}
          onChangeOrganization={handleOrganizationChange}
          onChangePage={handlePageChange}
          onClearFilters={handleClearFilters}
        />
      )}
      <Drawer
        title="Chi tiết tài khoản"
        open={drawerOpen}
        size={440}
        destroyOnHidden
        onClose={() => setSelectedId(null)}
        extra={
          detailState.status === "ready" && (
            <RoleNavActionWrapper accountId={detailState.detail.id}>
              {(onNav) => (
                <Button size="small" onClick={onNav}>
                  Vai trò & phạm vi
                </Button>
              )}
            </RoleNavActionWrapper>
          )
        }
      >
        {detailState.status === "loading" && (
          <div role="status" aria-label="Đang tải thông tin tài khoản">
            <Skeleton active paragraph={{ rows: 6 }} />
          </div>
        )}
        {detailState.status === "error" && (
          <Alert
            type="error"
            showIcon
            role="alert"
            title="Không thể tải thông tin tài khoản"
            description={detailState.requestId ? `Mã hỗ trợ: ${detailState.requestId}` : undefined}
            action={<Button onClick={() => setDetailNonce((nonce) => nonce + 1)}>Thử lại</Button>}
          />
        )}
        {detailState.status === "ready" && (
          <RoleNavActionWrapper accountId={detailState.detail.id}>
            {(onNav) => (
              <UserAccountDetailDrawer
                detail={detailState.detail}
                onAction={handleDrawerAction}
                identityPending={identityPending}
                onManageRoles={onNav}
              />
            )}
          </RoleNavActionWrapper>
        )}
      </Drawer>
      <CreateUserAccountModal
        open={createModalState !== null}
        organizationOptions={organizationOptions}
        submitting={createModalState?.submitting ?? false}
        error={createModalState?.error ?? null}
        onCancel={handleCancelCreate}
        onSubmit={handleCreateSubmit}
      />
      {lifecycleModalState && detailState.status === "ready" && (
        <UserAccountLifecycleModal
          open={lifecycleModalState.open}
          action={lifecycleModalState.action}
          accountName={lifecycleModalState.accountName}
          submitting={lifecycleModalState.submitting}
          error={lifecycleModalState.error}
          onCancel={handleCancelLifecycle}
          onSubmit={handleLifecycleSubmit}
          onReloadConflict={handleReloadLifecycle}
        />
      )}
      {passwordResetModalState && detailState.status === "ready" && (
        <UserAccountPasswordResetModal
          open={passwordResetModalState.open}
          submitting={passwordResetModalState.submitting}
          error={passwordResetModalState.error}
          onCancel={handleCancelPasswordReset}
          onConfirm={handlePasswordResetConfirm}
        />
      )}
    </>
  );
}
